// frontend/src/marketing/pages/legal/LegalPageLayout.tsx
import * as React from 'react';
import { Link } from 'react-router-dom';
import { MarketingPageHead } from '../../MarketingPageHead';

export type LegalSection = { heading: string; body: React.ReactNode };

/**
 * Shared shell for the four legal documents. Renders an eyebrow, title,
 * last-updated stamp, cross-links to the sibling documents, and a list of
 * numbered prose sections. Content pages stay pure-data.
 */
export function LegalPageLayout(props: {
  eyebrow: string;
  title: string;
  description: string;
  updated: string; // e.g. "June 4, 2026"
  sections: LegalSection[];
}) {
  return (
    <div className="overflow-x-hidden">
      <MarketingPageHead title={props.title} description={props.description} />
      <section className="pt-12 pb-20 lg:pt-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <p className="text-[11px] uppercase tracking-[0.16em] font-bold text-coral">{props.eyebrow}</p>
          <h1 className="mt-4 font-light text-4xl lg:text-5xl text-ink dark:text-white tracking-[-0.03em]">
            {props.title}
          </h1>
          <p className="mt-3 text-[13px] text-ink/50 dark:text-white/50">Last updated: {props.updated}</p>

          <nav className="mt-6 flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-ink/60 dark:text-white/60">
            <Link className="hover:text-ink dark:hover:text-white underline-offset-2 hover:underline" to="/legal/terms">Terms</Link>
            <Link className="hover:text-ink dark:hover:text-white underline-offset-2 hover:underline" to="/legal/privacy">Privacy</Link>
            <Link className="hover:text-ink dark:hover:text-white underline-offset-2 hover:underline" to="/legal/dpa">DPA</Link>
            <Link className="hover:text-ink dark:hover:text-white underline-offset-2 hover:underline" to="/legal/aup">Acceptable use</Link>
          </nav>

          <div className="mt-10 space-y-10">
            {props.sections.map((s, i) => (
              <section key={s.heading}>
                <h2 className="text-[17px] font-semibold text-ink dark:text-white">
                  {i + 1}. {s.heading}
                </h2>
                <div className="mt-3 space-y-3 text-[14.5px] leading-relaxed text-ink/70 dark:text-white/70">
                  {s.body}
                </div>
              </section>
            ))}
          </div>

          <p className="mt-14 text-[13px] text-ink/50 dark:text-white/50">
            Questions about this document? <Link to="/contact" className="underline underline-offset-2">Contact us</Link>.
          </p>
        </div>
      </section>
    </div>
  );
}
